import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Clock, Users, Edit, Trash2, ChefHat, Printer,
  Scale, Check, Minus, Plus, ExternalLink, ShoppingCart, Loader2, X, Copy,
  Star, Play, Pause, RotateCcw, Timer, MessageSquare, Save, CookingPot, Share2,
  RefreshCw, Flame, Refrigerator, Snowflake, Info, Volume2, VolumeX, Mic, MicOff, QrCode, Lock
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { PasswordDialog } from "@/components/PasswordDialog";
import { ThemeToggle } from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_IMAGES = {
  "Breads": "/category-fallbacks/breads.jpg",
  "Soups": "/category-fallbacks/soups.jpg",
  "Main Dishes": "/category-fallbacks/main-dishes.jpg",
  "Sides": "/category-fallbacks/sides.jpg",
  "Preserves": "/category-fallbacks/preserves.jpg",
  "Desserts": "/category-fallbacks/desserts.jpg"
};

const DEFAULT_RECIPE_IMAGE = "/category-fallbacks/default.jpg";

// Helper to save recently viewed
const saveRecentlyViewed = (recipeId) => {
  try {
    let recent = JSON.parse(localStorage.getItem('recentlyViewedRecipes') || '[]');
    recent = [recipeId, ...recent.filter(id => id !== recipeId)].slice(0, 10);
    localStorage.setItem('recentlyViewedRecipes', JSON.stringify(recent));
  } catch (e) {
    console.error('Error saving recently viewed:', e);
  }
};

// Parse time strings to seconds
const parseTimeToSeconds = (timeStr) => {
  const hourMatch = timeStr.match(/(\d+)\s*h(our)?s?/i);
  const minMatch = timeStr.match(/(\d+)\s*m(in(ute)?s?)?/i);
  const secMatch = timeStr.match(/(\d+)\s*s(ec(ond)?s?)?/i);
  
  let seconds = 0;
  if (hourMatch) seconds += parseInt(hourMatch[1]) * 3600;
  if (minMatch) seconds += parseInt(minMatch[1]) * 60;
  if (secMatch) seconds += parseInt(secMatch[1]);
  
  if (!hourMatch && !minMatch && !secMatch) {
    const numMatch = timeStr.match(/(\d+)/);
    if (numMatch) seconds = parseInt(numMatch[1]) * 60;
  }
  
  return seconds;
};

// Format seconds to display
const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Find times in instruction text
const findTimesInText = (text) => {
  const timeRegex = /(\d+)\s*(hours?|hrs?|minutes?|mins?|seconds?|secs?)/gi;
  const matches = [];
  let match;
  
  while ((match = timeRegex.exec(text)) !== null) {
    matches.push({
      text: match[0],
      index: match.index,
      seconds: parseTimeToSeconds(match[0])
    });
  }
  
  return matches;
};

// Scale quantities in instruction text
const scaleInstructionText = (text, multiplier) => {
  if (multiplier === 1) return text;
  
  // Match measurement patterns with optional words between number and unit
  // Handles: "2 cups", "1/2 teaspoon", "1.5 tablespoons", "just under 1 teaspoon", "about 2 cups"
  const measurementRegex = /(\d+\/\d+|\d+\.?\d*)\s*(cups?|tablespoons?|tbsp|teaspoons?|tsp|ounces?|oz|pounds?|lbs?|grams?|g|ml|liters?|l|quarts?|qt|pints?|pt|gallons?|gal)\b/gi;
  
  return text.replace(measurementRegex, (match, amount, unit) => {
    let numericAmount;
    
    // Handle fractions like "1/2"
    if (amount.includes('/')) {
      const [num, denom] = amount.split('/');
      numericAmount = parseFloat(num) / parseFloat(denom);
    } else {
      numericAmount = parseFloat(amount);
    }
    
    const scaled = numericAmount * multiplier;
    
    // Format the scaled amount nicely
    let formattedAmount;
    if (scaled === Math.floor(scaled)) {
      formattedAmount = scaled.toString();
    } else if (scaled < 1) {
      // Convert small decimals to fractions for readability
      const fractionMap = {
        0.25: '1/4', 0.33: '1/3', 0.5: '1/2', 0.67: '2/3', 0.75: '3/4'
      };
      const rounded = Math.round(scaled * 100) / 100;
      formattedAmount = fractionMap[rounded] || scaled.toFixed(2).replace(/\.?0+$/, '');
    } else {
      formattedAmount = scaled.toFixed(2).replace(/\.?0+$/, '');
    }
    
    return `${formattedAmount} ${unit}`;
  });
};

export default function RecipeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useMetric, setUseMetric] = useState(false);
  const [servingMultiplier, setServingMultiplier] = useState(1);
  const [checkedIngredients, setCheckedIngredients] = useState({});
  const [checkedInstructions, setCheckedInstructions] = useState({});
  const [shoppingList, setShoppingList] = useState(null);
  const [shoppingListOpen, setShoppingListOpen] = useState(false);
  const [generatingList, setGeneratingList] = useState(false);
  
  // Cook Mode state
  const [cookMode, setCookMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Timer state
  const [activeTimers, setActiveTimers] = useState([]);
  const timerIntervalRef = useRef(null);
  
  // Personal Notes state
  const [personalNotes, setPersonalNotes] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Substitute state
  const [substituteLoading, setSubstituteLoading] = useState(null);
  const [substituteData, setSubstituteData] = useState({});

  // Nutrition state
  const [nutrition, setNutrition] = useState(null);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [nutritionOpen, setNutritionOpen] = useState(false);

  // Voice Cook Mode state
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const speechSynthRef = useRef(null);
  const recognitionRef = useRef(null);

  // QR Code sharing state
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePwd, setDeletePwd] = useState("");
  const [deletingNow, setDeletingNow] = useState(false);

  // Helper to require auth for an action
  const requireAuth = (action) => {
    if (isAuthenticated) {
      action();
    } else {
      setPendingAction(() => action);
      setPasswordDialogOpen(true);
    }
  };

  useEffect(() => {
    fetchRecipe();
    
    let wakeLock = null;
    const requestWakeLock = async () => {
      if (cookMode && 'wakeLock' in navigator) {
        try {
          wakeLock = await navigator.wakeLock.request('screen');
        } catch (e) {
          console.log('Wake lock not available');
        }
      }
    };
    requestWakeLock();
    
    return () => {
      if (wakeLock) wakeLock.release();
    };
  }, [id, cookMode]);

  // Timer interval
  useEffect(() => {
    if (activeTimers.length > 0) {
      timerIntervalRef.current = setInterval(() => {
        setActiveTimers(prev => {
          const updated = prev.map(t => {
            if (t.running && t.remaining > 0) {
              const newRemaining = t.remaining - 1;
              if (newRemaining === 0) {
                const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQAw');
                audio.play().catch(() => {});
                toast.success(`Timer finished: ${t.label}`);
              }
              return { ...t, remaining: newRemaining };
            }
            return t;
          });
          return updated;
        });
      }, 1000);
    }
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [activeTimers.length]);

  // Text-to-speech function
  const speakText = useCallback((text) => {
    if (!voiceEnabled || !('speechSynthesis' in window)) return;
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    speechSynthRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [voiceEnabled]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, []);

  // Read current instruction aloud
  const readCurrentStep = useCallback(() => {
    if (!recipe || !cookMode) return;
    const stepText = `Step ${currentStep + 1}. ${recipe.instructions[currentStep]}`;
    speakText(stepText);
  }, [recipe, cookMode, currentStep, speakText]);

  // Voice recognition setup
  useEffect(() => {
    if (!cookMode || !voiceListening) return;
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice commands not supported in this browser");
      setVoiceListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const last = event.results.length - 1;
      const command = event.results[last][0].transcript.toLowerCase().trim();
      console.log('Voice command:', command);

      if (command.includes('next') || command.includes('forward')) {
        if (currentStep < recipe.instructions.length - 1) {
          setCurrentStep(prev => prev + 1);
          toast.success("Next step");
        }
      } else if (command.includes('previous') || command.includes('back') || command.includes('go back')) {
        if (currentStep > 0) {
          setCurrentStep(prev => prev - 1);
          toast.success("Previous step");
        }
      } else if (command.includes('repeat') || command.includes('again') || command.includes('read')) {
        readCurrentStep();
      } else if (command.includes('timer') || command.includes('start timer')) {
        // Find time in current instruction and start timer
        const timeMatch = recipe.instructions[currentStep].match(/(\d+)\s*(hours?|hrs?|minutes?|mins?|seconds?|secs?)/i);
        if (timeMatch) {
          const seconds = parseTimeToSeconds(timeMatch[0]);
          startTimer(seconds, `Step ${currentStep + 1}`);
          toast.success(`Timer started: ${timeMatch[0]}`);
        } else {
          toast.info("No time found in this step");
        }
      }
    };

    recognition.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        setVoiceListening(false);
      }
    };

    recognition.onend = () => {
      // Restart if still listening
      if (voiceListening && cookMode) {
        try {
          recognition.start();
        } catch (e) {
          console.log('Could not restart recognition');
        }
      }
    };

    recognitionRef.current = recognition;
    
    try {
      recognition.start();
      toast.success("Voice commands active", { description: "Say: next, previous, repeat, or start timer" });
    } catch (e) {
      console.log('Could not start recognition');
    }

    return () => {
      try {
        recognition.stop();
      } catch (e) {}
    };
  }, [cookMode, voiceListening, currentStep, recipe, readCurrentStep]);

  // Auto-read step when it changes (if voice enabled)
  useEffect(() => {
    if (voiceEnabled && cookMode && recipe) {
      readCurrentStep();
    }
  }, [currentStep, voiceEnabled, cookMode, recipe, readCurrentStep]);

  // Cleanup speech on unmount or exit cook mode
  useEffect(() => {
    return () => {
      stopSpeaking();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, [stopSpeaking]);

  // Public URL: prefer the revocable share token, fall back to the bare recipe URL.
  const getShareableUrl = () => {
    if (recipe?.share_token) {
      return `${window.location.origin}/share/${recipe.share_token}`;
    }
    return `${window.location.origin}/recipe/${id}`;
  };

  const createShareLink = async () => {
    try {
      const res = await axios.post(`${API}/recipes/${id}/share`, {}, { headers: getAuthHeaders() });
      setRecipe((prev) => ({ ...prev, share_token: res.data.share_token }));
      toast.success("Public share link created");
    } catch (err) {
      if (err.response?.status === 401) {
        requireAuth(createShareLink);
      } else {
        toast.error("Failed to create share link");
      }
    }
  };

  const revokeShareLink = async () => {
    try {
      await axios.delete(`${API}/recipes/${id}/share`, { headers: getAuthHeaders() });
      setRecipe((prev) => ({ ...prev, share_token: null }));
      toast.success("Share link revoked");
    } catch (err) {
      if (err.response?.status === 401) {
        requireAuth(revokeShareLink);
      } else {
        toast.error("Failed to revoke share link");
      }
    }
  };

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/recipes/${id}`);
      setRecipe(response.data);
      setPersonalNotes(response.data.personal_notes || "");
      saveRecentlyViewed(id);
    } catch (error) {
      console.error("Error fetching recipe:", error);
      toast.error("Recipe not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // Delete a recipe using a freshly-typed password (re-prompt). Sends the
  // password as the X-Admin-Password header on this one call only; localStorage
  // isn't touched, so the user's cached login is independent of this action.
  const handleDelete = async (freshPassword) => {
    try {
      await axios.delete(`${API}/recipes/${id}`, {
        headers: { "X-Admin-Password": freshPassword },
      });
      toast.success("Recipe deleted");
      navigate("/");
    } catch (error) {
      console.error("Error deleting recipe:", error);
      if (error.response?.status === 401) {
        toast.error("Wrong password");
      } else {
        toast.error("Failed to delete recipe");
      }
      throw error; // let the dialog stay open so user can retry
    }
  };

  const toggleFavorite = async () => {
    try {
      const response = await axios.post(`${API}/recipes/${id}/favorite`, {}, {
        headers: getAuthHeaders()
      });
      setRecipe(prev => ({ ...prev, is_favorite: response.data.is_favorite }));
      toast.success(response.data.is_favorite ? "Added to favorites" : "Removed from favorites");
    } catch (error) {
      console.error("Error toggling favorite:", error);
      if (error.response?.status === 401) {
        requireAuth(toggleFavorite);
      } else {
        toast.error("Failed to update favorite");
      }
    }
  };

  const savePersonalNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/recipes/${id}/personal-notes`, { personal_notes: personalNotes }, {
        headers: getAuthHeaders()
      });
      setRecipe(prev => ({ ...prev, personal_notes: personalNotes }));
      setEditingNotes(false);
      toast.success("Notes saved");
    } catch (error) {
      console.error("Error saving notes:", error);
      if (error.response?.status === 401) {
        requireAuth(savePersonalNotes);
      } else {
        toast.error("Failed to save notes");
      }
    } finally {
      setSavingNotes(false);
    }
  };

  const toggleIngredient = (index) => {
    setCheckedIngredients((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleInstruction = (index) => {
    setCheckedInstructions((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const scaleAmount = (amountStr) => {
    if (servingMultiplier === 1 || !amountStr) return amountStr;
    
    // Handle fractions like "1/2", "3/4", "1/3"
    const fractionMatch = amountStr.match(/^(\d+)\/(\d+)\s*(.*)/);
    if (fractionMatch) {
      const num = parseFloat(fractionMatch[1]) / parseFloat(fractionMatch[2]);
      const unit = fractionMatch[3];
      const scaled = num * servingMultiplier;
      
      // Convert back to nice fractions if possible
      const fractionMap = { 0.25: '1/4', 0.33: '1/3', 0.5: '1/2', 0.67: '2/3', 0.75: '3/4' };
      const rounded = Math.round(scaled * 100) / 100;
      const formatted = fractionMap[rounded] || (scaled === Math.floor(scaled) ? scaled.toString() : scaled.toFixed(2).replace(/\.?0+$/, ''));
      return `${formatted} ${unit}`;
    }
    
    // Handle decimals and whole numbers
    const match = amountStr.match(/^([\d.]+)\s*(.*)/);
    if (match) {
      const num = parseFloat(match[1]);
      const unit = match[2];
      const scaled = (num * servingMultiplier).toFixed(2).replace(/\.?0+$/, '');
      return `${scaled} ${unit}`;
    }
    return amountStr;
  };

  const handlePrint = () => {
    window.print();
  };

  const generateShoppingList = async () => {
    setGeneratingList(true);
    try {
      const response = await axios.post(`${API}/shopping-list`, {
        recipe_ids: [id],
        multiplier: servingMultiplier
      });
      setShoppingList(response.data);
      setShoppingListOpen(true);
    } catch (error) {
      console.error("Error generating shopping list:", error);
      toast.error("Failed to generate shopping list");
    } finally {
      setGeneratingList(false);
    }
  };

  const copyShoppingList = () => {
    if (!shoppingList) return;
    
    let text = `Shopping List for ${recipe.title}`;
    if (servingMultiplier !== 1) text += ` (${servingMultiplier}x)`;
    text += "\n\n";
    
    for (const section of shoppingList.sections) {
      text += `${section.name}\n`;
      for (const item of section.items) {
        text += `  • ${item.item}`;
        if (item.note) text += ` (${item.note})`;
        text += "\n";
      }
      text += "\n";
    }
    
    navigator.clipboard.writeText(text);
    toast.success("Shopping list copied to clipboard");
  };

  const exportRecipe = async () => {
    const text = formatRecipeAsText();
    
    if (navigator.share) {
      try {
        await navigator.share({ title: recipe.title, text: text });
        return;
      } catch (e) {}
    }
    
    navigator.clipboard.writeText(text);
    toast.success("Recipe copied to clipboard");
  };

  const formatRecipeAsText = () => {
    let text = `${recipe.title}\n`;
    text += "=".repeat(recipe.title.length) + "\n\n";
    
    if (recipe.source) text += `Source: ${recipe.source}\n`;
    if (recipe.prep_time) text += `Prep Time: ${recipe.prep_time}\n`;
    if (recipe.cook_time) text += `Cook Time: ${recipe.cook_time}\n`;
    if (recipe.servings) text += `Servings: ${Math.round(recipe.servings * servingMultiplier)}\n`;
    text += "\n";
    
    text += "INGREDIENTS\n";
    text += "-----------\n";
    recipe.ingredients.forEach(ing => {
      const amount = useMetric 
        ? scaleAmount(ing.amount_metric) 
        : scaleAmount(ing.amount_imperial);
      text += `• ${amount} ${ing.name}`;
      if (ing.notes) text += ` (${ing.notes})`;
      text += "\n";
    });
    text += "\n";
    
    text += "INSTRUCTIONS\n";
    text += "------------\n";
    recipe.instructions.forEach((inst, i) => {
      text += `${i + 1}. ${inst}\n\n`;
    });
    
    if (recipe.notes) {
      text += "NOTES\n";
      text += "-----\n";
      text += recipe.notes + "\n";
    }
    
    text += "\n— Shared from Burnsy's Bistro Recipes";
    
    return text;
  };

  const getSubstitute = async (ingredient, index) => {
    setSubstituteLoading(index);
    try {
      const response = await axios.post(`${API}/ingredient-substitute`, {
        ingredient: `${ingredient.amount_imperial} ${ingredient.name}`,
        recipe_context: recipe.title
      });
      setSubstituteData(prev => ({ ...prev, [index]: response.data }));
    } catch (error) {
      console.error("Error getting substitute:", error);
      toast.error("Failed to get substitutes");
    } finally {
      setSubstituteLoading(null);
    }
  };

  const fetchNutrition = async () => {
    setNutritionLoading(true);
    try {
      const response = await axios.post(`${API}/recipe-nutrition`, {
        recipe_id: id,
        servings_multiplier: servingMultiplier
      });
      setNutrition(response.data);
      setNutritionOpen(true);
    } catch (error) {
      console.error("Error getting nutrition:", error);
      toast.error("Failed to get nutrition info");
    } finally {
      setNutritionLoading(false);
    }
  };

  const startTimer = (seconds, label) => {
    const newTimer = {
      id: Date.now(),
      label,
      total: seconds,
      remaining: seconds,
      running: true
    };
    setActiveTimers(prev => [...prev, newTimer]);
    toast.success(`Timer started: ${label}`);
  };

  const toggleTimer = (timerId) => {
    setActiveTimers(prev => prev.map(t => 
      t.id === timerId ? { ...t, running: !t.running } : t
    ));
  };

  const resetTimer = (timerId) => {
    setActiveTimers(prev => prev.map(t => 
      t.id === timerId ? { ...t, remaining: t.total, running: false } : t
    ));
  };

  const removeTimer = (timerId) => {
    setActiveTimers(prev => prev.filter(t => t.id !== timerId));
  };

  // Render instruction with clickable times and scaled quantities
  const renderInstructionWithTimers = (text, stepIndex, shouldScale = false) => {
    // First, scale quantities in the text if multiplier is active
    const scaledText = shouldScale ? scaleInstructionText(text, servingMultiplier) : text;
    
    const times = findTimesInText(scaledText);
    if (times.length === 0) return scaledText;
    
    const parts = [];
    let lastIndex = 0;
    
    times.forEach((time, i) => {
      if (time.index > lastIndex) {
        parts.push(scaledText.slice(lastIndex, time.index));
      }
      parts.push(
        <button
          key={`time-${stepIndex}-${i}`}
          onClick={(e) => {
            e.stopPropagation();
            startTimer(time.seconds, `Step ${stepIndex + 1}: ${time.text}`);
          }}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-full text-sm font-medium transition-colors"
          data-testid={`timer-btn-${stepIndex}-${i}`}
        >
          <Timer className="w-3 h-3" strokeWidth={2} />
          {time.text}
        </button>
      );
      lastIndex = time.index + time.text.length;
    });
    
    if (lastIndex < scaledText.length) {
      parts.push(scaledText.slice(lastIndex));
    }
    
    return parts;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <ChefHat className="w-6 h-6 text-primary animate-pulse" strokeWidth={1.5} />
          </div>
          <p className="text-muted-foreground">Loading recipe...</p>
        </div>
      </div>
    );
  }

  if (!recipe) return null;

  const recipeImage = recipe.image_data || recipe.image_url || CATEGORY_IMAGES[recipe.category] || DEFAULT_RECIPE_IMAGE;

  // Cook Mode View
  if (cookMode) {
    const currentInstruction = recipe.instructions[currentStep];
    
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="bg-primary text-primary-foreground p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CookingPot className="w-6 h-6" strokeWidth={1.5} />
            <span className="font-serif text-lg font-semibold">Cook Mode</span>
          </div>
          
          {/* Voice Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (isSpeaking) {
                  stopSpeaking();
                } else {
                  readCurrentStep();
                }
              }}
              className="text-primary-foreground hover:bg-primary-foreground/20"
              data-testid="voice-read-btn"
              title={isSpeaking ? "Stop reading" : "Read step aloud"}
            >
              {isSpeaking ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className={`text-primary-foreground hover:bg-primary-foreground/20 ${voiceEnabled ? 'bg-primary-foreground/20' : ''}`}
              data-testid="auto-read-btn"
              title={voiceEnabled ? "Auto-read ON" : "Auto-read OFF"}
            >
              <span className="text-xs mr-1">Auto</span>
              {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVoiceListening(!voiceListening)}
              className={`text-primary-foreground hover:bg-primary-foreground/20 ${voiceListening ? 'bg-primary-foreground/20 animate-pulse' : ''}`}
              data-testid="voice-command-btn"
              title={voiceListening ? "Voice commands ON" : "Voice commands OFF"}
            >
              {voiceListening ? (
                <Mic className="w-5 h-5" />
              ) : (
                <MicOff className="w-5 h-5" />
              )}
            </Button>

            <Separator orientation="vertical" className="h-6 bg-primary-foreground/30" />
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                stopSpeaking();
                setVoiceListening(false);
                setCookMode(false);
              }}
              className="text-primary-foreground hover:bg-primary-foreground/20"
              data-testid="exit-cook-mode"
            >
              <X className="w-5 h-5 mr-1" />
              Exit
            </Button>
          </div>
        </header>

        {/* Voice Commands Hint */}
        {voiceListening && (
          <div className="bg-accent/20 border-b border-accent/30 px-4 py-2 text-center text-sm">
            <span className="text-accent-foreground">
              🎤 Listening... Say: <strong>"next"</strong>, <strong>"previous"</strong>, <strong>"repeat"</strong>, or <strong>"start timer"</strong>
            </span>
          </div>
        )}

        {activeTimers.length > 0 && (
          <div className="bg-secondary p-3 flex gap-3 overflow-x-auto">
            {activeTimers.map(timer => (
              <div 
                key={timer.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-full ${
                  timer.remaining === 0 ? 'bg-accent text-accent-foreground animate-pulse' : 'bg-card'
                }`}
              >
                <Timer className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-mono font-medium">{formatTime(timer.remaining)}</span>
                <button onClick={() => toggleTimer(timer.id)} className="p-1 hover:bg-muted rounded-full">
                  {timer.running ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                </button>
                <button onClick={() => resetTimer(timer.id)} className="p-1 hover:bg-muted rounded-full">
                  <RotateCcw className="w-3 h-3" />
                </button>
                <button onClick={() => removeTimer(timer.id)} className="p-1 hover:bg-muted rounded-full text-destructive">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <main className="flex-1 flex flex-col lg:flex-row">
          {/* Ingredients Panel */}
          <div className="lg:w-80 bg-card border-b lg:border-b-0 lg:border-r border-border p-4 lg:p-6 overflow-y-auto max-h-48 lg:max-h-none">
            <h3 className="font-serif text-lg font-semibold mb-3 flex items-center gap-2">
              <Scale className="w-4 h-4" strokeWidth={1.5} />
              Ingredients
            </h3>
            <ul className="space-y-2">
              {recipe.ingredients.map((ingredient, index) => {
                const amount = useMetric 
                  ? scaleAmount(ingredient.amount_metric) 
                  : scaleAmount(ingredient.amount_imperial);
                
                return (
                  <li 
                    key={index}
                    className={`flex items-start gap-2 text-sm ${
                      checkedIngredients[index] ? "opacity-50 line-through" : ""
                    }`}
                  >
                    <Checkbox
                      id={`cook-ingredient-${index}`}
                      checked={checkedIngredients[index] || false}
                      onCheckedChange={() => toggleIngredient(index)}
                      className="mt-0.5"
                    />
                    <label htmlFor={`cook-ingredient-${index}`} className="cursor-pointer">
                      <span className="font-medium">{amount}</span>{" "}
                      <span>{ingredient.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {servingMultiplier !== 1 && (
              <p className="text-xs text-muted-foreground mt-3">
                Scaled to {servingMultiplier}x
              </p>
            )}
          </div>

          {/* Instructions Panel */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-8 text-center">
            <div className="text-muted-foreground mb-4">
              Step {currentStep + 1} of {recipe.instructions.length}
            </div>
            
            <p className="text-xl md:text-2xl lg:text-3xl font-serif leading-relaxed max-w-3xl">
              {renderInstructionWithTimers(currentInstruction, currentStep, true)}
            </p>
            
            {servingMultiplier !== 1 && (
              <div className="mt-4 text-sm text-muted-foreground bg-secondary/50 px-3 py-1.5 rounded-full">
                Quantities scaled to {servingMultiplier}x
              </div>
            )}
            
            <div className="flex items-center gap-4 mt-8 lg:mt-12">
              <Button
                variant="outline"
                size="lg"
                onClick={() => setCurrentStep(s => Math.max(0, s - 1))}
                disabled={currentStep === 0}
                className="rounded-full px-6 lg:px-8"
                data-testid="prev-step"
              >
                Previous
              </Button>
              
              <Button
                size="lg"
                onClick={() => {
                  if (currentStep < recipe.instructions.length - 1) {
                    setCurrentStep(s => s + 1);
                  } else {
                    setCookMode(false);
                    toast.success("Recipe complete! 🎉");
                  }
                }}
                className="rounded-full px-6 lg:px-8"
                data-testid="next-step"
              >
                {currentStep === recipe.instructions.length - 1 ? "Finish" : "Next"}
              </Button>
            </div>
          </div>
        </main>

        {/* Step Indicators */}
        <div className="p-4 flex justify-center gap-2">
          {recipe.instructions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentStep(idx)}
              className={`w-3 h-3 rounded-full transition-colors ${
                idx === currentStep ? 'bg-primary' : idx < currentStep ? 'bg-primary/50' : 'bg-muted'
              }`}
              data-testid={`step-indicator-${idx}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Password Dialog */}
      <PasswordDialog 
        open={passwordDialogOpen} 
        onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open) setPendingAction(null);
        }}
        onSuccess={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border no-print">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </Link>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={toggleFavorite}
              data-testid="favorite-btn"
            >
              <Star 
                className={`w-5 h-5 ${recipe.is_favorite ? "text-accent fill-accent" : ""}`} 
                strokeWidth={1.5} 
              />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full"
              onClick={exportRecipe}
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full"
              onClick={() => setQrDialogOpen(true)}
              data-testid="qr-share-btn"
              title="Share via QR Code"
            >
              <QrCode className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full"
              onClick={handlePrint}
              data-testid="print-btn"
            >
              <Printer className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full"
              onClick={generateShoppingList}
              disabled={generatingList}
              data-testid="shopping-list-btn"
            >
              {generatingList ? (
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
              ) : (
                <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
              )}
            </Button>
            <Link to={`/recipe/${id}/edit`}>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="edit-btn">
                <Edit className="w-5 h-5" strokeWidth={1.5} />
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full text-destructive hover:text-destructive"
              data-testid="delete-btn"
              onClick={() => {
                setDeletePwd("");
                setDeleteDialogOpen(true);
              }}
            >
              <Trash2 className="w-5 h-5" strokeWidth={1.5} />
            </Button>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-destructive" />
                    Delete Recipe
                  </DialogTitle>
                  <DialogDescription>
                    Type your admin password to permanently delete <strong>{recipe.title}</strong>. This cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!deletePwd) return;
                    setDeletingNow(true);
                    try {
                      await handleDelete(deletePwd);
                      // success → navigation already happened
                    } catch {
                      setDeletePwd("");
                    } finally {
                      setDeletingNow(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <Input
                    type="password"
                    autoFocus
                    value={deletePwd}
                    onChange={(e) => setDeletePwd(e.target.value)}
                    placeholder="Enter admin password to confirm"
                    data-testid="delete-password-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={!deletePwd || deletingNow}
                      className="flex-1 rounded-full"
                      data-testid="confirm-delete-btn"
                    >
                      {deletingNow ? "Deleting…" : "Delete forever"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDeleteDialogOpen(false)}
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Hero Image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img
          src={recipeImage}
          alt={recipe.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = DEFAULT_RECIPE_IMAGE; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
      </div>

      {/* Active Timers Floating Bar */}
      {activeTimers.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-card border border-border rounded-full shadow-lg p-2 flex gap-2">
          {activeTimers.map(timer => (
            <div 
              key={timer.id}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                timer.remaining === 0 ? 'bg-accent text-accent-foreground animate-pulse' : 'bg-secondary'
              }`}
            >
              <Timer className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-mono text-sm font-medium">{formatTime(timer.remaining)}</span>
              <button onClick={() => toggleTimer(timer.id)} className="p-1 hover:bg-background/50 rounded-full">
                {timer.running ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              </button>
              <button onClick={() => removeTimer(timer.id)} className="p-1 hover:bg-background/50 rounded-full">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 -mt-20 relative z-10 pb-16">
        {/* Title Section */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge variant="secondary">{recipe.category}</Badge>
            {(recipe.tags || []).map((t) => (
              <Badge
                key={t}
                variant="outline"
                className="rounded-full text-xs cursor-pointer hover:bg-secondary"
                onClick={() => navigate(`/?tag=${encodeURIComponent(t)}`)}
                data-testid={`tag-${t}`}
              >
                #{t}
              </Badge>
            ))}
            {recipe.is_favorite && (
              <Star className="w-4 h-4 text-accent fill-accent" strokeWidth={1.5} />
            )}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4" data-testid="recipe-title">
            {recipe.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
            {recipe.prep_time && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" strokeWidth={1.5} />
                Prep: {recipe.prep_time}
              </span>
            )}
            {recipe.cook_time && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" strokeWidth={1.5} />
                Cook: {recipe.cook_time}
              </span>
            )}
            {recipe.servings && (
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4" strokeWidth={1.5} />
                {Math.round(recipe.servings * servingMultiplier)} servings
              </span>
            )}
          </div>

          {recipe.source && (
            <p className="mt-3 text-sm text-muted-foreground flex items-center gap-1.5">
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
              Source: {recipe.source}
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              onClick={() => { setCookMode(true); setCurrentStep(0); }}
              className="rounded-full gap-2"
              data-testid="start-cook-mode"
            >
              <CookingPot className="w-4 h-4" strokeWidth={1.5} />
              Start Cooking
            </Button>
            <Button
              variant="outline"
              onClick={fetchNutrition}
              disabled={nutritionLoading}
              className="rounded-full gap-2"
              data-testid="nutrition-btn"
            >
              {nutritionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Flame className="w-4 h-4" strokeWidth={1.5} />
              )}
              Nutrition & Storage
            </Button>
          </div>
        </div>

        {/* Ingredients Section - Now First */}
        <section className="mb-8">
          <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
            {/* Controls Row */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 no-print">
              <h2 className="font-serif text-2xl font-semibold">Ingredients</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor="measurement-toggle" className="text-sm font-medium flex items-center gap-2">
                    <Scale className="w-4 h-4" strokeWidth={1.5} />
                    Metric
                  </Label>
                  <Switch
                    id="measurement-toggle"
                    checked={useMetric}
                    onCheckedChange={setUseMetric}
                    data-testid="measurement-toggle"
                  />
                </div>
                <Separator orientation="vertical" className="h-6" />
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-8 h-8 rounded-full"
                    onClick={() => setServingMultiplier((prev) => Math.max(0.5, prev - 0.5))}
                    disabled={servingMultiplier <= 0.5}
                    data-testid="scale-down-btn"
                  >
                    <Minus className="w-4 h-4" strokeWidth={2} />
                  </Button>
                  <span className="w-12 text-center font-medium" data-testid="scale-value">
                    {servingMultiplier}x
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-8 h-8 rounded-full"
                    onClick={() => setServingMultiplier((prev) => prev + 0.5)}
                    data-testid="scale-up-btn"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2} />
                  </Button>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mb-4">Tap any ingredient for substitutes</p>
            
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recipe.ingredients.map((ingredient, index) => {
                const amount = useMetric 
                  ? scaleAmount(ingredient.amount_metric) 
                  : scaleAmount(ingredient.amount_imperial);
                
                return (
                  <li 
                    key={index}
                    className={`flex items-start gap-3 ${
                      checkedIngredients[index] ? "opacity-50" : ""
                    }`}
                  >
                    <Checkbox
                      id={`ingredient-${index}`}
                      checked={checkedIngredients[index] || false}
                      onCheckedChange={() => toggleIngredient(index)}
                      className="mt-0.5"
                      data-testid={`ingredient-check-${index}`}
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <button 
                          className={`text-sm text-left cursor-pointer hover:text-primary transition-colors ${
                            checkedIngredients[index] ? "line-through" : ""
                          }`}
                          onClick={() => !substituteData[index] && getSubstitute(ingredient, index)}
                          data-testid={`ingredient-substitute-${index}`}
                        >
                          <span className="font-medium">{amount}</span>
                          {" "}
                          <span className="text-foreground">{ingredient.name}</span>
                          {ingredient.notes && (
                            <span className="text-muted-foreground"> ({servingMultiplier !== 1 ? scaleInstructionText(ingredient.notes, servingMultiplier) : ingredient.notes})</span>
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80" align="start">
                        {substituteLoading === index ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                            <span className="ml-2 text-sm">Finding substitutes...</span>
                          </div>
                        ) : substituteData[index] ? (
                          <div className="space-y-3">
                            <div className="font-medium flex items-center gap-2">
                              <RefreshCw className="w-4 h-4 text-primary" strokeWidth={1.5} />
                              Substitutes for {ingredient.name}
                            </div>
                            <div className="space-y-2">
                              {substituteData[index].substitutes?.map((sub, sIdx) => (
                                <div key={sIdx} className="p-2 bg-secondary/50 rounded-lg">
                                  <div className="font-medium text-sm">{sub.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {sub.ratio} • {sub.notes}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {substituteData[index].tip && (
                              <p className="text-xs text-muted-foreground italic">
                                💡 {substituteData[index].tip}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-muted-foreground">
                            Click to find substitutes
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </li>
                );
              })}
            </ul>

            {/* Shopping List Button */}
            <Button
              onClick={generateShoppingList}
              disabled={generatingList}
              className="mt-6 rounded-full gap-2"
              variant="outline"
              data-testid="generate-shopping-list-btn"
            >
              {generatingList ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4" strokeWidth={1.5} />
                  Shopping List
                </>
              )}
            </Button>
          </div>
        </section>

        {/* Instructions - Now Second */}
        <section className="mb-8">
          <h2 className="font-serif text-2xl font-semibold mb-6 flex items-center justify-between">
            <span>Instructions</span>
            {servingMultiplier !== 1 && (
              <span className="text-sm font-normal text-muted-foreground bg-secondary/50 px-3 py-1 rounded-full">
                Quantities scaled to {servingMultiplier}x
              </span>
            )}
          </h2>
          <div className="space-y-4">
            {recipe.instructions.map((instruction, index) => (
              <div 
                key={index} 
                className={`flex gap-4 p-4 rounded-xl transition-all ${
                  checkedInstructions[index] ? "bg-muted/50 opacity-60" : "bg-card"
                }`}
              >
                <button
                  onClick={() => toggleInstruction(index)}
                  className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-all ${
                    checkedInstructions[index] 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-primary/10 text-primary"
                  }`}
                  data-testid={`instruction-check-${index}`}
                >
                  {checkedInstructions[index] ? (
                    <Check className="w-4 h-4" strokeWidth={2} />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </button>
                <p className={`text-foreground leading-relaxed ${
                  checkedInstructions[index] ? "line-through" : ""
                }`}>
                  {renderInstructionWithTimers(instruction, index, servingMultiplier !== 1)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Recipe Notes */}
        {recipe.notes && (
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-semibold mb-4">Recipe Notes</h2>
            <div className="p-4 bg-secondary/50 rounded-xl">
              <p className="text-foreground whitespace-pre-wrap">
                {servingMultiplier !== 1 ? scaleInstructionText(recipe.notes, servingMultiplier) : recipe.notes}
              </p>
            </div>
          </section>
        )}

        {/* Personal Notes */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5" strokeWidth={1.5} />
              My Notes
            </h2>
            {!editingNotes && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setEditingNotes(true)}
                className="rounded-full"
                data-testid="edit-personal-notes"
              >
                <Edit className="w-4 h-4 mr-1" strokeWidth={1.5} />
                Edit
              </Button>
            )}
          </div>
          
          {editingNotes ? (
            <div className="space-y-3">
              <Textarea
                value={personalNotes}
                onChange={(e) => setPersonalNotes(e.target.value)}
                placeholder="Add your own notes... (e.g., 'reduce sugar next time', 'kids loved this!')"
                className="min-h-[120px]"
                data-testid="personal-notes-input"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={savePersonalNotes}
                  disabled={savingNotes}
                  className="rounded-full gap-2"
                  data-testid="save-personal-notes"
                >
                  {savingNotes ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" strokeWidth={1.5} />
                  )}
                  Save
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => { setEditingNotes(false); setPersonalNotes(recipe.personal_notes || ""); }}
                  className="rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : personalNotes ? (
            <div className="p-4 bg-accent/10 border border-accent/20 rounded-xl">
              <p className="text-foreground whitespace-pre-wrap">{personalNotes}</p>
            </div>
          ) : (
            <button
              onClick={() => setEditingNotes(true)}
              className="w-full p-4 border-2 border-dashed border-border rounded-xl text-muted-foreground hover:border-primary hover:text-primary transition-colors"
              data-testid="add-personal-notes"
            >
              + Add personal notes for next time
            </button>
          )}
        </section>
      </main>

      {/* Shopping List Dialog */}
      <Dialog open={shoppingListOpen} onOpenChange={setShoppingListOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
              Shopping List
            </DialogTitle>
          </DialogHeader>
          
          {shoppingList && (
            <div className="space-y-6">
              <div className="text-sm text-muted-foreground">
                For: {recipe.title} {servingMultiplier !== 1 && `(${servingMultiplier}x)`}
              </div>
              
              {shoppingList.sections.map((section, sIdx) => (
                <div key={sIdx}>
                  <h3 className="font-semibold text-primary mb-2">{section.name}</h3>
                  <ul className="space-y-1.5">
                    {section.items.map((item, iIdx) => (
                      <li key={iIdx} className="text-sm flex items-start gap-2">
                        <span className="text-muted-foreground">•</span>
                        <span>
                          {item.item}
                          {item.note && (
                            <span className="text-muted-foreground"> ({item.note})</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={copyShoppingList}
                  className="flex-1 rounded-full gap-2"
                  data-testid="copy-shopping-list-btn"
                >
                  <Copy className="w-4 h-4" strokeWidth={1.5} />
                  Copy to Clipboard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShoppingListOpen(false)}
                  className="rounded-full"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Nutrition & Storage Dialog */}
      <Dialog open={nutritionOpen} onOpenChange={setNutritionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Flame className="w-5 h-5 text-accent" strokeWidth={1.5} />
              Nutrition & Storage
            </DialogTitle>
          </DialogHeader>
          
          {nutrition && (
            <div className="space-y-6">
              {/* Nutrition Panel */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4" strokeWidth={1.5} />
                  Per Serving (estimated)
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-secondary/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary">{nutrition.per_serving?.calories}</div>
                    <div className="text-xs text-muted-foreground">Calories</div>
                  </div>
                  <div className="p-3 bg-secondary/50 rounded-lg text-center">
                    <div className="text-2xl font-bold">{nutrition.per_serving?.protein_g}g</div>
                    <div className="text-xs text-muted-foreground">Protein</div>
                  </div>
                  <div className="p-3 bg-secondary/50 rounded-lg text-center">
                    <div className="text-2xl font-bold">{nutrition.per_serving?.carbs_g}g</div>
                    <div className="text-xs text-muted-foreground">Carbs</div>
                  </div>
                  <div className="p-3 bg-secondary/50 rounded-lg text-center">
                    <div className="text-2xl font-bold">{nutrition.per_serving?.fat_g}g</div>
                    <div className="text-xs text-muted-foreground">Fat</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
                  <div className="p-2 bg-muted/50 rounded">
                    <span className="font-medium">{nutrition.per_serving?.fiber_g}g</span>
                    <span className="text-muted-foreground text-xs block">Fiber</span>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <span className="font-medium">{nutrition.per_serving?.sugar_g}g</span>
                    <span className="text-muted-foreground text-xs block">Sugar</span>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <span className="font-medium">{nutrition.per_serving?.sodium_mg}mg</span>
                    <span className="text-muted-foreground text-xs block">Sodium</span>
                  </div>
                </div>
                {nutrition.notes && (
                  <p className="mt-3 text-xs text-muted-foreground italic">
                    💡 {nutrition.notes}
                  </p>
                )}
              </div>

              <Separator />

              {/* Storage Info */}
              <div>
                <h3 className="font-semibold mb-3">Storage</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                    <Refrigerator className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                    <div>
                      <div className="font-medium">Refrigerator</div>
                      <div className="text-sm text-muted-foreground">{nutrition.storage?.refrigerator}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                    <Snowflake className="w-5 h-5 text-cyan-500" strokeWidth={1.5} />
                    <div>
                      <div className="font-medium">Freezer</div>
                      <div className="text-sm text-muted-foreground">{nutrition.storage?.freezer}</div>
                    </div>
                  </div>
                </div>
                {nutrition.storage?.tips && (
                  <p className="mt-3 text-sm text-muted-foreground">
                    📦 {nutrition.storage.tips}
                  </p>
                )}
              </div>

              <Button
                variant="outline"
                onClick={() => setNutritionOpen(false)}
                className="w-full rounded-full"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Share Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center justify-center gap-2">
              <QrCode className="w-5 h-5" strokeWidth={1.5} />
              Share Recipe
            </DialogTitle>
            <DialogDescription>
              {recipe.share_token
                ? "Public share link active — revoke any time to break the URL."
                : "Create a revocable public link, then share it via QR or copy."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center py-6">
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              <QRCodeSVG
                value={getShareableUrl()}
                size={200}
                level="M"
                includeMargin={false}
              />
            </div>
            <p className="mt-4 font-serif text-lg font-semibold">{recipe.title}</p>
            <p className="text-sm text-muted-foreground mt-1 break-all px-4">
              {getShareableUrl()}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                navigator.clipboard.writeText(getShareableUrl());
                toast.success("Link copied to clipboard!");
              }}
              className="flex-1 rounded-full gap-2"
              data-testid="copy-qr-link-btn"
            >
              <Copy className="w-4 h-4" strokeWidth={1.5} />
              Copy Link
            </Button>
            {recipe.share_token ? (
              <Button
                variant="destructive"
                onClick={revokeShareLink}
                className="rounded-full"
                data-testid="revoke-share-link-btn"
              >
                Revoke
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={createShareLink}
                className="rounded-full"
                data-testid="create-share-link-btn"
              >
                Create public link
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => setQrDialogOpen(false)}
              className="rounded-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
